import React from "react";
import { useTranslate } from "@refinedev/core";
import {
  Form,
  Input,
  Switch,
  Typography,
  Divider,
  Row,
  Col,
  Tag,
  Button,
  theme,
} from "antd";
import { usePageStyles } from "../../shared/styles";

const { Title, Text } = Typography;

/**
 * The tenant fields that both the tenant-admin view and the superadmin tenant
 * editor need. Defining them once means a label, tooltip or validation change
 * lands in every view at the same time — previously there were three copies
 * (`settings/GeneralSettingsPage`, `tenants/TenantGeneralTab`, `tenants/show`)
 * and they had already drifted.
 */

export type FieldVariant =
  /** Rich cards with descriptions and status dots — the tenant-admin view. */
  | "rich"
  /** Compact two-column grid — the superadmin tenant editor. */
  | "compact"
  /** Read-only display — the tenant show page. */
  | "readonly";

interface FieldsProps {
  variant: FieldVariant;
  /** Required for `variant="readonly"`. */
  record?: Record<string, any>;
  /** Opens the API credentials modal — rich automation only. */
  onManageApiKeys?: () => void;
  /** Hide section title/subtitle when fields are embedded in a modal. */
  hideHeader?: boolean;
}

const secretMask = "••••••••";

/* ------------------------------------------------------------------ */
/* Automation                                                          */
/* ------------------------------------------------------------------ */

const AUTOMATION_FIELDS = [
  { name: "autodDecryptFiles", labelKey: "tenants.fields.autodDecryptFiles", descKey: "autoDecryptDescription" },
  { name: "autoAssembleFiles", labelKey: "tenants.fields.autoAssembleFiles", descKey: "autoAssembleDescription" },
] as const;

export const TenantAutomationFields: React.FC<FieldsProps> = ({
  variant,
  record,
  onManageApiKeys,
}) => {
  const _t = useTranslate();
  const { token } = theme.useToken();
  const pageStyles = usePageStyles();
  const form = Form.useFormInstance();
  const s = (key: string) => _t(`settings.generalSettingsCard.${key}`);

  if (variant === "readonly") {
    return (
      <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
        <Col xs={24}>
          <Title level={5}>
            {_t("tenants.sections.fileProcessing", "File Processing Settings")}
          </Title>
        </Col>
        {AUTOMATION_FIELDS.map((f) => (
          <Col xs={24} sm={12} md={12} key={f.name}>
            <Text strong>{_t(f.labelKey)}: </Text>
            <Tag color={record?.[f.name] ? "green" : "default"}>
              {record?.[f.name] ? s("statusActive") : s("statusInactive")}
            </Tag>
          </Col>
        ))}
      </Row>
    );
  }

  if (variant === "compact") {
    return (
      <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
        {AUTOMATION_FIELDS.map((f) => (
          <Col xs={24} sm={12} md={12} key={f.name}>
            <Form.Item
              label={_t(f.labelKey)}
              name={f.name}
              valuePropName="checked"
              tooltip={s(f.descKey)}
            >
              <Switch />
            </Form.Item>
          </Col>
        ))}
      </Row>
    );
  }

  // rich
  const borderColor = pageStyles.summaryDividerColor;
  return (
    <>
      <Title level={4} className="!mb-1 !font-semibold" style={{ color: token.colorText }}>
        {s("automationTitle")}
      </Title>
      <Text className="text-sm block mb-6" style={{ color: token.colorTextSecondary }}>
        {s("automationSubtitle")}
      </Text>

      {AUTOMATION_FIELDS.map((f, idx) => (
        <React.Fragment key={f.name}>
          {idx > 0 && <Divider className="my-0" />}
          <div
            className="flex items-start justify-between gap-4 py-4 border-b"
            style={{ borderColor }}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-medium" style={{ color: token.colorText }}>
                  {_t(f.labelKey)}
                </span>
              </div>
              <Text className="text-sm block mb-2" style={{ color: token.colorTextSecondary }}>
                {s(f.descKey)}
              </Text>
              <Form.Item shouldUpdate noStyle>
                {() => {
                  const on = form.getFieldValue(f.name);
                  return (
                    <div className="flex items-center gap-1.5 text-sm">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: on ? "#52C41A" : token.colorTextTertiary }}
                      />
                      <span style={{ color: token.colorTextSecondary }}>
                        {on ? s("statusActive") : s("statusInactive")}
                      </span>
                    </div>
                  );
                }}
              </Form.Item>
              {f.name === "autodDecryptFiles" && onManageApiKeys && (
                <Button type="link" className="!px-0 !h-auto mt-2" onClick={onManageApiKeys}>
                  {s("manageApiKeys")}
                </Button>
              )}
            </div>
            <Form.Item name={f.name} valuePropName="checked" noStyle>
              <Switch className="!min-w-[44px] shrink-0" />
            </Form.Item>
          </div>
        </React.Fragment>
      ))}
    </>
  );
};

/* ------------------------------------------------------------------ */
/* API keys                                                            */
/* ------------------------------------------------------------------ */

export const TenantApiKeyFields: React.FC<FieldsProps> = ({
  variant,
  record,
  hideHeader = false,
}) => {
  const _t = useTranslate();
  const { token } = theme.useToken();
  const s = (key: string) => _t(`settings.generalSettingsCard.${key}`);

  if (variant === "readonly") {
    return (
      <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
        <Col xs={24}>
          <Title level={5}>
            {_t("tenants.sections.apiKeys", "API Keys & Credentials")}
          </Title>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <Text strong>{_t("tenants.fields.bFlashApiKey")}: </Text>
          <Text type="secondary">
            {record?.bFlashApiKey ? secretMask : _t("tenants.fields.notSet", "Not set")}
          </Text>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <Text strong>{_t("tenants.fields.autotunerMastId")}: </Text>
          <Text type="secondary">
            {record?.autotunerMastId
              ? String(record.autotunerMastId)
              : _t("tenants.fields.notSet", "Not set")}
          </Text>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <Text strong>{_t("tenants.fields.autotunerApiKey")}: </Text>
          <Text type="secondary">
            {record?.autotunerApiKey ? secretMask : _t("tenants.fields.notSet", "Not set")}
          </Text>
        </Col>
      </Row>
    );
  }

  if (variant === "compact") {
    return (
      <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
        <Col xs={24} sm={12} md={12}>
          <Form.Item
            label={_t("tenants.fields.bFlashApiKey")}
            name="bFlashApiKey"
            tooltip={s("bFlashDescription")}
          >
            <Input.Password autoComplete="off" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <Form.Item
            label={_t("tenants.fields.autotunerMastId")}
            name="autotunerMastId"
            tooltip={s("autotunerMastDescription")}
          >
            <Input placeholder={s("autotunerMastPlaceholder")} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <Form.Item
            label={_t("tenants.fields.autotunerApiKey")}
            name="autotunerApiKey"
            tooltip={s("autotunerKeyDescription")}
          >
            <Input.Password
              autoComplete="off"
              placeholder={s("autotunerKeyPlaceholder")}
            />
          </Form.Item>
        </Col>
      </Row>
    );
  }

  // rich
  return (
    <>
      {!hideHeader && (
        <>
          <Title level={4} className="!mb-1 !font-semibold" style={{ color: token.colorText }}>
            {s("apiKeysTitle")}
          </Title>
          <Text className="text-sm block mb-6" style={{ color: token.colorTextSecondary }}>
            {s("apiKeysSubtitle")}
          </Text>
        </>
      )}

      <div className="space-y-6">
        <Form.Item
          name="bFlashApiKey"
          className="!mb-6"
          label={_t("tenants.fields.bFlashApiKey")}
          extra={s("bFlashDescription")}
        >
          <Input.TextArea autoComplete="off" className="rounded-lg" rows={8} />
        </Form.Item>

        <Divider className="my-0" />

        <Form.Item
          name="autotunerMastId"
          className="!mb-6"
          label={_t("tenants.fields.autotunerMastId")}
          extra={s("autotunerMastDescription")}
        >
          <Input placeholder={s("autotunerMastPlaceholder")} className="rounded-lg" />
        </Form.Item>

        <Form.Item
          name="autotunerApiKey"
          className="!mb-0"
          label={_t("tenants.fields.autotunerApiKey")}
          extra={s("autotunerKeyDescription")}
        >
          <Input.TextArea
            placeholder={s("autotunerKeyPlaceholder")}
            autoComplete="off"
            className="rounded-lg"
            rows={8}
          />
        </Form.Item>
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Announcement                                                        */
/* ------------------------------------------------------------------ */

export const TenantAnnouncementFields: React.FC = () => {
  const _t = useTranslate();
  const { token } = theme.useToken();

  return (
    <>
      <Title level={4} className="!mb-1 !font-semibold" style={{ color: token.colorText }}>
        {_t("settings.announcementTab.title")}
      </Title>
      <Text className="text-sm block mb-6" style={{ color: token.colorTextSecondary }}>
        {_t("settings.announcementTab.subtitle")}
      </Text>
      <Form.Item
        name="announcementMessage"
        className="!mb-0"
        label={_t("settings.announcementTab.messageLabel")}
        extra={_t("settings.announcementTab.messageHelp")}
      >
        <Input.TextArea
          className="rounded-lg"
          rows={5}
          maxLength={2000}
          showCount
          placeholder={_t("settings.announcementTab.messagePlaceholder")}
        />
      </Form.Item>
    </>
  );
};
